import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  Box,
  Avatar,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateField } from "@mui/x-date-pickers/DateField";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import LoadingButton from "@mui/lab/LoadingButton";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import { CapSentence, handleKeyDownField } from "../utils/Utils";
import Enumerable from "linq";
import crypto from "../Crypto";

const ESPECIALIDAD_ORTODONCIA_ID = 8;

const validationSchema = yup.object({
  fecha_desde: yup
    .date()
    .nullable()
    .typeError("Fecha inválida.")
    .required("Campo obligatorio."),
  fecha_hasta: yup
    .date()
    .nullable()
    .typeError("Fecha inválida.")
    .min(yup.ref("fecha_desde"), "Fecha hasta debe ser mayor o igual a desde.")
    .required("Campo obligatorio."),
  clinica: yup.object().nullable().required("Campo obligatorio."),
});

export default function OrtodonciaFiltros({
  onBuscar,
  loading,
  gastoBrackets,
  porcentajePago,
  onActualizarParametro,
}) {
  const [odontologosLista, setOdontologosLista] = useState([]);
  const allCentros = crypto.decryptDataStorage("centros") || [];

  const [modalOpen, setModalOpen] = useState(false);
  const [modalParam, setModalParam] = useState(null);
  const [modalValor, setModalValor] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const fechadesdeInputRef = useRef(null);
  const fechahastaInputRef = useRef(null);

  useEffect(() => {
    if (fechadesdeInputRef.current) {
      fechadesdeInputRef.current.focus();
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      fecha_desde: dayjs(Date()),
      fecha_hasta: dayjs(Date()),
      clinica: null,
      odontologo: null,
    },
    validationSchema,
    onSubmit: (values) => onBuscar(values),
  });

  const onClinicasChange = (event, value) => {
    formik.setFieldValue("clinica", value);
    formik.setFieldValue("odontologo", null);
    setOdontologosLista([]);
    if (!value) return;
    const allCoes = crypto.decryptDataStorage("allcoes") || [];
    const odontologos = Enumerable.from(allCoes)
      .where(
        (w) =>
          w.centro_id === value.id &&
          w.especialidad_id === ESPECIALIDAD_ORTODONCIA_ID,
      )
      .groupBy((g) => g.odontologo_id)
      .select((e) => ({ id: e.key(), odontologo: e.max((m) => m.odontologo) }))
      .toArray();
    setOdontologosLista(odontologos);
  };

  function abrirModal(parametro) {
    setModalParam(parametro);
    setModalValor(parametro?.valor ?? "");
    setModalError("");
    setModalOpen(true);
  }

  function cerrarModal() {
    setModalOpen(false);
    setModalParam(null);
    setModalValor("");
    setModalError("");
  }

  async function handleGuardar() {
    const num = parseFloat(modalValor);
    if (isNaN(num) || num <= 0) {
      setModalError("Ingresá un valor numérico mayor a 0.");
      return;
    }
    setModalLoading(true);
    const result = await onActualizarParametro(modalParam, String(num));
    setModalLoading(false);
    if (result.error === false) {
      cerrarModal();
    } else {
      setModalError("Error al guardar. Intentá nuevamente.");
    }
  }

  const esBrackets = modalParam?.codigo_tabla === "GASTOS_BRACKETS";

  return (
    <>
      {/* Parámetros discretos */}
      <Box sx={{ display: "flex", gap: 5, mb: 2 }}>
        {porcentajePago && (
          <Box
            onClick={() => abrirModal(porcentajePago)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Honorarios
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              {porcentajePago.valor}%
            </Typography>
            <EditIcon sx={{ fontSize: 12, color: "text.secondary" }} />
          </Box>
        )}
        {gastoBrackets && (
          <Box
            onClick={() => abrirModal(gastoBrackets)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Brackets
            </Typography>
            <Typography variant="body2" color="primary" fontWeight="bold">
              $ {gastoBrackets.valor}
            </Typography>
            <EditIcon sx={{ fontSize: 12, color: "text.secondary" }} />
          </Box>
        )}
      </Box>
      <Grid
        container
        spacing={2}
        alignItems="center"
        sx={{ paddingBottom: 2, borderBottom: 1, borderColor: "grey.400" }}
      >
        {/* Fecha desde */}
        <Grid item xs={12} md={6} lg={2}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-gb"
          >
            <DateField
              id="fecha_desde"
              label="Desde"
              inputRef={(el) => (fechadesdeInputRef.current = el)}
              value={formik.values.fecha_desde}
              format="DD/MM/YYYY"
              onChange={(value) => formik.setFieldValue("fecha_desde", value)}
              size="small"
              helperText={
                formik.touched.fecha_desde ? formik.errors.fecha_desde : ""
              }
              error={
                formik.touched.fecha_desde && Boolean(formik.errors.fecha_desde)
              }
              onKeyDown={(e) => handleKeyDownField(e, fechahastaInputRef)}
            />
          </LocalizationProvider>
        </Grid>

        {/* Fecha hasta */}
        <Grid item xs={12} md={6} lg={2}>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-gb"
          >
            <DateField
              id="fecha_hasta"
              label="Hasta"
              inputRef={(el) => (fechahastaInputRef.current = el)}
              value={formik.values.fecha_hasta}
              format="DD/MM/YYYY"
              onChange={(value) => formik.setFieldValue("fecha_hasta", value)}
              size="small"
              helperText={formik.errors.fecha_hasta}
              error={Boolean(formik.errors.fecha_hasta)}
            />
          </LocalizationProvider>
        </Grid>

        {/* Clínica */}
        <Grid item xs={12} md={6} lg={2}>
          <Autocomplete
            id="clinica"
            options={allCentros}
            value={formik.values.clinica}
            autoHighlight
            size="small"
            onChange={onClinicasChange}
            getOptionLabel={(option) => option.razonsocial}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: option.color,
                    marginBottom: 0.5,
                    marginRight: 1,
                    border: 1,
                    borderColor: "black",
                  }}
                >
                  {option.identificador}
                </Avatar>
                {option.razonsocial}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Clínica"
                variant="outlined"
                error={formik.touched.clinica && Boolean(formik.errors.clinica)}
                helperText={formik.touched.clinica && formik.errors.clinica}
              />
            )}
          />
        </Grid>

        {/* Odontólogo */}
        <Grid item xs={12} md={6} lg={2}>
          <Autocomplete
            id="odontologo"
            options={odontologosLista}
            value={formik.values.odontologo}
            autoHighlight
            size="small"
            disabled={!formik.values.clinica}
            onChange={(event, value) =>
              formik.setFieldValue("odontologo", value)
            }
            getOptionLabel={(option) => CapSentence(option.odontologo)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Odontólogo (opcional)"
                variant="outlined"
              />
            )}
          />
        </Grid>

        {/* Botón buscar */}
        <Grid item xs={12} lg={1}>
          <LoadingButton
            size="medium"
            onClick={formik.handleSubmit}
            loading={loading}
            loadingPosition="end"
            endIcon={<SearchIcon />}
            sx={{
              backgroundColor: "primary.main",
              ":hover": { bgcolor: "#475569", color: "white" },
              width: 80,
              marginTop: { xs: 1, md: 0 },
            }}
            variant="contained"
          >
            <span></span>
          </LoadingButton>
        </Grid>
      </Grid>

      {/* Modal */}
      <Dialog open={modalOpen} onClose={cerrarModal} maxWidth="xs" fullWidth>
        <DialogTitle>
          {esBrackets ? "Gasto de Brackets" : "Porcentaje Honorarios"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={esBrackets ? "Importe" : "Porcentaje"}
            value={modalValor}
            onChange={(e) => {
              setModalValor(e.target.value);
              setModalError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleGuardar()}
            InputProps={{
              startAdornment: esBrackets ? (
                <InputAdornment position="start">$</InputAdornment>
              ) : undefined,
              endAdornment: !esBrackets ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined,
            }}
            error={Boolean(modalError)}
            helperText={modalError}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarModal} color="inherit">
            Cancelar
          </Button>
          <LoadingButton
            onClick={handleGuardar}
            loading={modalLoading}
            variant="contained"
          >
            Guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
